import { Preferences } from '@capacitor/preferences';
import {
	eventRegistrationPageUrl,
	featureWalkthroughPageUrl,
	maxPageShowUpTimes,
	preferenceKeys,
	setupAlertPageUrl
} from '../core/constants';

// Checks whether a page should still be shown or has reached its max show limit
export const getPageShowFlag = async (registeredKey: string, shownCountKey: string) => {
	const { value: registeredValue } = await Preferences.get({ key: registeredKey });
	if (registeredValue === 'true') {
		return true; // User has registered, don't show anymore
	}

	// If not registered, check whether it has already been shown the maximum number of times
	const { value: shownCountValue } = await Preferences.get({ key: shownCountKey });
	const shownCount = parseInt(shownCountValue || '0', 10);

	if (shownCount < maxPageShowUpTimes) {
		await Preferences.set({ key: shownCountKey, value: (shownCount + 1).toString() });
		return false; // Show the page
	}

	return true; // Already shown max times, don't show anymore
};

export const getAndSetAppFeatureWalkthroughShownFlag = async () => {
	const { value } = await Preferences.get({ key: preferenceKeys.hasAppFeatureShown });
	if (!value) {
		await Preferences.set({ key: preferenceKeys.hasAppFeatureShown, value: 'true' });
		return false;
	}
	return true;
};

export const getNotificationEnabledFlag = async () => {
	return getPageShowFlag(preferenceKeys.hasNotificationEnabled, preferenceKeys.notificationPromptShownCount);
};

export const setNotificationEnabledFlag = async () => {
	await Preferences.set({ key: preferenceKeys.hasNotificationEnabled, value: 'true' });
};

export const getEventRegisteredFlag = async () => {
	return getPageShowFlag(preferenceKeys.hasEventRegistered, preferenceKeys.eventRegistrationShownCount);
};

export const setEventRegisteredFlag = async () => {
	await Preferences.set({ key: preferenceKeys.hasEventRegistered, value: 'true' });
};

// Configuration for initial launch pages in sequence
export const getInitialLaunchPageConfigs = () => {
	return [
		{
			url: featureWalkthroughPageUrl,
			checkFunction: getAndSetAppFeatureWalkthroughShownFlag
		},
		{
			url: setupAlertPageUrl,
			checkFunction: getNotificationEnabledFlag
		},
		{
			url: eventRegistrationPageUrl,
			checkFunction: getEventRegisteredFlag
		}
		// Add new pages here following the same pattern
	];
};

/**
 * Check which initial launch page should be shown to the user based on the sequence
 * Returns the page URL to navigate to, or null if no page should be shown
 */
export const getInitialLaunchPageToShow = async (canShowSetupPushNotificationPrompt: boolean) => {
	const pageConfigs = getInitialLaunchPageConfigs();

	if (!canShowSetupPushNotificationPrompt) {
		// Filter out the setup alert page if we can't show it
		const filteredConfigs = pageConfigs.filter((config) => config.url !== setupAlertPageUrl);
		pageConfigs.length = 0; // Clear original array
		pageConfigs.push(...filteredConfigs); // Add filtered configs back
	}

	// Check pages in order and return the first one that should be shown
	const checkNextPage = async (index: number): Promise<string | null> => {
		if (index >= pageConfigs.length) {
			return null;
		}

		const pageConfig = pageConfigs[index];
		const hasPageShown = await pageConfig.checkFunction();

		if (!hasPageShown) {
			return pageConfig.url;
		}

		// Check next page
		return checkNextPage(index + 1);
	};

	return checkNextPage(0);
};
